/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import { PanoramaView as PerxPanoramaView } from '@perxteam/react-panorama-view';
import cn from 'classnames';
import Panolens from '@perxteam/react-panorama-view/lib/panolens/panolens';
import '@perxteam/react-panorama-view/lib/style.css';
import detectScreenType, { TScreenType } from '../../utils/detectScreenType';
import detectTouchScreen from '../../utils/detectTouchScreen';
import calculatePanoramaHeight from '../../utils/calculatePanoramaHeight';
import debounce from '../../utils/debounce';
import { get } from './utils';
import { processHotspots, THotspot, hotspotWithCoords } from './hotspots';
import * as vehiclesApi from '../../api/vehicles';
import style from './style.module.scss';
import PanolensUtils from './PanolensUtils';

declare type TPanoramaViewProps = {
  img: string | HTMLImageElement;
  vehicleId: string;
  width?: string;
  height?: string;
  customClass?: string;
  title?: string;
};

export const pathsToGrade: Record<string, any> = {
  vitara: [
    'vehicles',
    { id: 'vitara' },
    'modifications',
    { id: 'novaya-vitara' },
    'grades',
    { id: 'glx' },
  ],
  jimny: [
    'vehicles',
    { id: 'jimny' },
    'modifications',
    { id: 'all-new-jimny' },
    'grades',
    { id: 'glx' },
  ],
  sx4: [
    'vehicles',
    { id: 'sx4' },
    'modifications',
    { id: 'sx4-fl' },
    'grades',
    { id: 'glx' },
  ],
};

const panoImageTypes: Record<TScreenType, string> = {
  desktop: 'panoImageDesktop',
  mobile: 'panoImageMobile',
  retina: 'panoImageRetina',
};

let clickedInfoSpots: any = [];
const resetClickedInfospots = () => {
  clickedInfoSpots.forEach(spot => {
    spot.element.style.display = 'none';
    PanolensUtils.showWithAnimation(spot);
  });
  clickedInfoSpots = [];
};

const PanoramaView: React.FC<TPanoramaViewProps> = props => {
  const { height, width, customClass, img, title, vehicleId } = props;

  const [panoramaLoaded, setPanoramaLoaded] = React.useState(false);
  const [panoramaClicked, setPanoramaClicked] = React.useState(false);
  const [hotspots, setHotspots] = React.useState<THotspot[]>([]);
  const [imagePath, setImagePath] = React.useState<any>();

  const calculatedPanoramaHeight = React.useMemo(
    () => calculatePanoramaHeight(),
    []
  );
  const [containerHeight, setContainerHeight] = React.useState<string>(
    height || calculatedPanoramaHeight
  );

  const screenType = React.useMemo(() => detectScreenType(), []);
  const isTouchScreen = React.useMemo(() => detectTouchScreen(), []);
  const stumbIsActive = panoramaLoaded && !panoramaClicked;

  const handleResize = () => {
    if (height) {
      return;
    }

    const newHeight = calculatePanoramaHeight();
    setContainerHeight(newHeight);
  };

  // TODO: экспортировать тип TInfohotstop
  const infospotHoverStartHandler = ({ infospot }) => {
    PanolensUtils.hideWithAnimation(infospot);
  };
  const onInfospotHoverEndHandler = ({ infospot }) => {
    infospot.unlockHoverElement();
    PanolensUtils.showWithAnimation(infospot);
  };

  const onInfospotClick = ({ infospot }) => {
    const spot = infospot;
    if (!isTouchScreen) return;
    resetClickedInfospots();
    PanolensUtils.hideWithAnimation(infospot);
    spot.focus();
    spot.element.style.display = 'block';
    clickedInfoSpots.push(spot);
  };

  const onContainerTouch = () => {
    // Костыль для touch девайсов - выливается из 2-ух проблем
    // 1) onTouchStart, повешенный на panoramaWrapper не вызывается на всём элементе(почему?), а только при клике на саму заглушку
    // 2) Данная функция-обработчик похоже мемоизируется под капотом у panolens, поэтому не обновляются внешние переменные
    const stubmElem = document.querySelector(`.${style.panoramaLogo}`);
    if (isTouchScreen && stubmElem) {
      setPanoramaClicked(true);
    }

    resetClickedInfospots();
  };

  const loadedHandler = (panolens: Panolens) => {
    panolens.viewer.container.addEventListener('touchstart', onContainerTouch);
    // Ограничивает максимальное отдаление камеры
    panolens.viewer.OrbitControls.maxFov = 60;

    PanolensUtils.panolensInstance = panolens;
    PanolensUtils.createHotspotTextures(panolens);
    setPanoramaLoaded(true);
  };

  const clickHandler = () => {
    if (stumbIsActive) {
      setPanoramaClicked(true);
    }
  };

  React.useEffect(() => {
    const debounceFn = debounce(100, handleResize);
    window.addEventListener('resize', debounceFn);

    return () => {
      window.removeEventListener('resize', debounceFn);
      PanolensUtils.panolensInstance.viewer.container.removeEventListener(
        'touchstart',
        onContainerTouch
      );
    };
  }, []);

  React.useEffect(() => {
    const fetchVehicles = async () => {
      const vehicles = await vehiclesApi.fetchVehicles();

      const grade = get(vehicles, pathsToGrade[vehicleId]);
      const spots = get(grade, ['panoPoints']);
      const imageFromApi = get(grade, [panoImageTypes[screenType]]);
      setImagePath(imageFromApi || img);
      setHotspots(processHotspots(spots, hotspotWithCoords[vehicleId]));
    };

    fetchVehicles();
  }, []);

  return (
    <div className={style.wrapper}>
      <div className="container">
        {title && <h2 className={style.title}>{title}</h2>}
        <div
          className={style.panoramaWrapper}
          onMouseDown={clickHandler}
          onTouchStart={clickHandler}
        >
          {stumbIsActive && (
            <div className={style.panoramaLogo}>
              {!isTouchScreen && (
                <>
                  Используйте мышь <br /> для просмотра панорамы
                </>
              )}
            </div>
          )}
          <PerxPanoramaView
            height={containerHeight}
            width={width}
            img={imagePath}
            hotspots={isTouchScreen || panoramaClicked ? hotspots : []}
            customClass={cn(style.panorama, customClass)}
            onLoaded={loadedHandler}
            onInfospotHoverStart={infospotHoverStartHandler}
            onInfospotHoverEnd={onInfospotHoverEndHandler}
            onInfospotClick={onInfospotClick}
            cameraZoom={screenType === 'mobile' ? 0.9 : 1}
          />
        </div>
      </div>
    </div>
  );
};

export default PanoramaView;
